import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiFlask, mdiLoading, mdiCreation } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import {
    useComplianceControls,
    useCreateComplianceRule,
    usePreviewRule,
    useRequestRuleDraft,
    useRuleDraft,
    type ComplianceControl,
    type RulePreview,
} from '../../hooks/useComplianceApi'
import { useDocumentTypes, usePropertyDefinitions } from '../../hooks/useQueries'
import {
    DRAFT_OPERATORS,
    DRAFT_SHAPES,
    EMPTY_FORM,
    TIMES_PER_YEAR,
    UNIVERSES,
    compileRuleForm,
    formFromDraft,
    timesPerYear,
    type RuleForm,
    type ServerDraft,
    type Universe,
} from './rule-catalog'

const SELECT_CLASS = 'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs'

function localized(m: Record<string, string> | undefined, lang: string): string {
    if (!m) return ''
    return m[lang] || m.fr || m.en || Object.values(m)[0] || ''
}

/**
 * "Nouvelle règle" (spec 2026-10-27-21 § 5): a guided dialog with three
 * shapes compiled to the existing condition vocabulary, a sentence a model
 * turns into a draft (never saved on its own), and "Tester", which counts
 * the assets the rule applies to and fails without writing anything.
 */
export function RuleCreateDialog({
    org,
    types,
    defaultControlUid,
    onClose,
}: {
    org: string
    types: { uid: string; code: string; name?: Record<string, string> | null }[]
    defaultControlUid?: string
    onClose: () => void
}) {
    const { t, i18n } = useTranslation('compliance')
    const lang = i18n.language
    const { data: controls = [] } = useComplianceControls(org)
    const { data: dateProperties = [] } = usePropertyDefinitions(org, { entityType: 'asset', dataTypes: ['date'] })
    const { data: properties = [] } = usePropertyDefinitions(org, { entityType: 'asset' })
    const { data: documentTypes = [] } = useDocumentTypes(org)
    const create = useCreateComplianceRule(org)
    const preview = usePreviewRule(org)
    const requestDraft = useRequestRuleDraft(org)

    const [form, setForm] = useState<RuleForm>(EMPTY_FORM)
    const [controlUid, setControlUid] = useState(defaultControlUid ?? '')
    const [assetTypeUid, setAssetTypeUid] = useState('')
    const [prompt, setPrompt] = useState('')
    const [jobUid, setJobUid] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [result, setResult] = useState<RulePreview | null>(null)
    const draft = useRuleDraft(org, jobUid)

    useEffect(() => {
        if (!controlUid && controls.length > 0) setControlUid((controls as ComplianceControl[])[0].uid)
    }, [controls, controlUid])

    // A model's draft opens in the form, every field editable.
    useEffect(() => {
        const d = draft.data
        if (!d || d.status === 'pending') return
        if (d.draft) {
            setForm((prev) => formFromDraft(d.draft as ServerDraft, prev))
            const code = (d.draft as ServerDraft).asset_type_code
            const type = code ? types.find((ty) => ty.code === code) : undefined
            if (type) setAssetTypeUid(type.uid)
        }
        setError(d.error ? t('business_rules.create.draft_refused', { reason: d.error }) : null)
        setJobUid(null)
    }, [draft.data, types, t])

    const set = <K extends keyof RuleForm>(key: K, value: RuleForm[K]) => {
        setForm((f) => ({ ...f, [key]: value }))
        setResult(null)
    }

    const compiled = useMemo(() => compileRuleForm(form), [form])
    const body = () => {
        if ('error' in compiled) return null
        return {
            ...compiled,
            compliance_control_uid: controlUid,
            asset_type_uid: assetTypeUid || undefined,
        }
    }

    const test = () => {
        const b = body()
        if (!b) {
            setError(t(`business_rules.create.invalid.${(compiled as { error: string }).error}`))
            return
        }
        setError(null)
        preview.mutate(b, { onSuccess: setResult, onError: () => setError(t('business_rules.create.test_error')) })
    }

    const save = () => {
        const b = body()
        if (!b || !controlUid) {
            setError(t(`business_rules.create.invalid.${'error' in compiled ? compiled.error : 'control'}`))
            return
        }
        setError(null)
        create.mutate(b, {
            onSuccess: onClose,
            onError: (e: unknown) => setError((e as { title?: string })?.title ?? t('business_rules.create.save_error')),
        })
    }

    const askModel = () => {
        if (!prompt.trim()) return
        setError(null)
        requestDraft.mutate(
            { prompt: prompt.trim(), locale: lang },
            {
                onSuccess: (r) => setJobUid(r.job_uid),
                onError: () => setError(t('business_rules.create.draft_error')),
            },
        )
    }
    const drafting = requestDraft.isPending || !!jobUid

    const n = timesPerYear(form.months)

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="rule-create-dialog">
                <DialogHeader>
                    <DialogTitle>{t('business_rules.create.title')}</DialogTitle>
                    <DialogDescription>{t('business_rules.create.description')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                    <label className="text-sm font-medium" htmlFor="rule-create-prompt">{t('business_rules.create.prompt_label')}</label>
                    <Textarea
                        id="rule-create-prompt"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder={t('business_rules.create.prompt_placeholder')}
                        rows={2}
                        data-testid="rule-create-prompt"
                    />
                    <div className="flex justify-end">
                        <Button size="sm" variant="outline" onClick={askModel} disabled={!prompt.trim() || drafting} data-testid="rule-create-prompt-submit">
                            <AppIcon path={drafting ? mdiLoading : mdiCreation} size="sm" className={drafting ? 'motion-safe:animate-spin' : ''} />
                            {t('business_rules.create.prompt_submit')}
                        </Button>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2" role="radiogroup">
                    {DRAFT_SHAPES.map((s) => (
                        <Button
                            key={s}
                            size="sm"
                            variant={form.shape === s ? 'default' : 'outline'}
                            onClick={() => set('shape', s)}
                            aria-pressed={form.shape === s}
                            data-testid={`rule-create-shape-${s}`}
                        >
                            {t(`business_rules.create.shape.${s}`)}
                        </Button>
                    ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <label className="space-y-1 text-sm">
                        <span>{t('business_rules.create.label_fr')}</span>
                        <Input value={form.labelFr} onChange={(e) => set('labelFr', e.target.value)} data-testid="rule-create-label-fr" />
                    </label>
                    <label className="space-y-1 text-sm">
                        <span>{t('business_rules.create.label_en')}</span>
                        <Input value={form.labelEn} onChange={(e) => set('labelEn', e.target.value)} data-testid="rule-create-label-en" />
                    </label>
                    <label className="space-y-1 text-sm">
                        <span>{t('business_rules.create.description_fr')}</span>
                        <Input value={form.descriptionFr} onChange={(e) => set('descriptionFr', e.target.value)} data-testid="rule-create-description-fr" />
                    </label>
                    <label className="space-y-1 text-sm">
                        <span>{t('business_rules.create.description_en')}</span>
                        <Input value={form.descriptionEn} onChange={(e) => set('descriptionEn', e.target.value)} data-testid="rule-create-description-en" />
                    </label>
                    <label className="space-y-1 text-sm">
                        <span>{t('business_rules.create.control')}</span>
                        <select className={SELECT_CLASS} value={controlUid} onChange={(e) => setControlUid(e.target.value)} data-testid="rule-create-control">
                            {(controls as ComplianceControl[]).map((c) => (
                                <option key={c.uid} value={c.uid}>{localized(c.name, lang) || c.code}</option>
                            ))}
                        </select>
                    </label>
                    <label className="space-y-1 text-sm">
                        <span>{t('business_rules.create.asset_type')}</span>
                        <select className={SELECT_CLASS} value={assetTypeUid} onChange={(e) => { setAssetTypeUid(e.target.value); setResult(null) }} data-testid="rule-create-asset-type">
                            <option value="">{t('business_rules.create.any_type')}</option>
                            {types.map((ty) => (
                                <option key={ty.uid} value={ty.uid}>{localized(ty.name ?? undefined, lang) || ty.code}</option>
                            ))}
                        </select>
                    </label>
                </div>

                {form.shape === 'recurring_obligation' && (
                    <div className="grid grid-cols-2 gap-3" data-testid="rule-create-recurring">
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.create.date_property')}</span>
                            <Input list="rule-create-date-properties" value={form.property} onChange={(e) => set('property', e.target.value)} data-testid="rule-create-property" />
                            <datalist id="rule-create-date-properties">
                                {dateProperties.map((p) => <option key={p.uid} value={p.code}>{localized(p.name as Record<string, string>, lang)}</option>)}
                            </datalist>
                        </label>
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.create.document_types')}</span>
                            <Input
                                list="rule-create-document-types"
                                value={form.documentTypes.join(', ')}
                                onChange={(e) => set('documentTypes', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                                data-testid="rule-create-document-types"
                            />
                            <datalist id="rule-create-document-types">
                                {documentTypes.map((d) => <option key={d.uid} value={d.code} />)}
                            </datalist>
                        </label>
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.recurrence.label')}</span>
                            <select
                                className={SELECT_CLASS}
                                value={n ? String(n) : 'custom'}
                                onChange={(e) => { if (e.target.value !== 'custom') set('months', 12 / Number(e.target.value)) }}
                                data-testid="rule-create-times-per-year"
                            >
                                {TIMES_PER_YEAR.map((x) => (
                                    <option key={x} value={x}>{t('business_rules.recurrence.times_per_year', { count: x })}</option>
                                ))}
                                <option value="custom">{t('business_rules.recurrence.custom')}</option>
                            </select>
                        </label>
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.recurrence.every_months')}</span>
                            <Input type="number" min={1} value={form.months} onChange={(e) => set('months', Number(e.target.value))} data-testid="rule-create-months" />
                        </label>
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.recurrence.warn')}</span>
                            <Input type="number" min={0} value={form.warnWindowDays} onChange={(e) => set('warnWindowDays', Number(e.target.value))} data-testid="rule-create-warn" />
                        </label>
                        <label className="flex items-center gap-2 self-end text-sm">
                            <Checkbox checked={form.inspection} onCheckedChange={(v) => set('inspection', v)} data-testid="rule-create-inspection" />
                            {t('business_rules.create.inspection')}
                        </label>
                    </div>
                )}

                {form.shape === 'occurrence_threshold' && (
                    <div className="grid grid-cols-3 gap-3" data-testid="rule-create-threshold">
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.create.intervention_kind')}</span>
                            <select className={SELECT_CLASS} value={form.interventionKind} onChange={(e) => set('interventionKind', e.target.value)} data-testid="rule-create-kind">
                                <option value="">{t('business_rules.create.kind_all')}</option>
                                {['corrective', 'preventive', 'works'].map((k) => (
                                    <option key={k} value={k}>{t(`business_rules.create.kind.${k}`)}</option>
                                ))}
                            </select>
                        </label>
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.create.count')}</span>
                            <Input type="number" min={0} value={form.count} onChange={(e) => set('count', Number(e.target.value))} data-testid="rule-create-count" />
                        </label>
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.create.window')}</span>
                            <Input type="number" min={1} value={form.windowMonths} onChange={(e) => set('windowMonths', Number(e.target.value))} data-testid="rule-create-window" />
                        </label>
                    </div>
                )}

                {form.shape === 'data_value' && (
                    <div className="grid grid-cols-3 gap-3" data-testid="rule-create-data-value">
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.create.property')}</span>
                            <Input list="rule-create-properties" value={form.property} onChange={(e) => set('property', e.target.value)} data-testid="rule-create-value-property" />
                            <datalist id="rule-create-properties">
                                {properties.map((p) => <option key={p.uid} value={p.code} />)}
                            </datalist>
                        </label>
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.create.operator')}</span>
                            <select className={SELECT_CLASS} value={form.operator} onChange={(e) => set('operator', e.target.value)} data-testid="rule-create-operator">
                                {DRAFT_OPERATORS.map((o) => <option key={o} value={o}>{t(`business_rules.create.op.${o}`)}</option>)}
                            </select>
                        </label>
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.create.value')}</span>
                            <Input value={form.value} onChange={(e) => set('value', e.target.value)} data-testid="rule-create-value" />
                        </label>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1 text-sm">
                        <span>{t('business_rules.create.universes')}</span>
                        <div className="flex flex-wrap gap-3">
                            {UNIVERSES.map((u: Universe) => (
                                <label key={u} className="flex items-center gap-1.5 text-xs">
                                    <Checkbox
                                        checked={form.universes.includes(u)}
                                        onCheckedChange={(v) => set('universes', v ? [...form.universes, u] : form.universes.filter((x) => x !== u))}
                                        data-testid={`rule-create-universe-${u}`}
                                    />
                                    {t(`business_rules.universe.${u}`)}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="space-y-1 text-sm">
                            <span>{t('business_rules.create.severity')}</span>
                            <select className={SELECT_CLASS} value={form.severity} onChange={(e) => set('severity', Number(e.target.value))} data-testid="rule-create-severity">
                                {[1, 2, 3, 4, 5].map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </label>
                        <label className="flex items-center gap-2 self-end text-sm">
                            <Checkbox checked={form.autoCreateIssue} onCheckedChange={(v) => set('autoCreateIssue', v)} data-testid="rule-create-ticket" />
                            {t('business_rules.create.ticket')}
                        </label>
                    </div>
                </div>

                {result && (
                    <p className="rounded-md bg-muted/40 p-2 text-sm" data-testid="rule-create-preview">
                        {result.sweep_produced
                            ? t('business_rules.create.preview_sweep')
                            : t('business_rules.create.preview', {
                                applicable: result.applicable,
                                non_compliant: result.non_compliant,
                                evaluated: result.evaluated,
                            })}
                        {result.truncated && ` ${t('business_rules.create.preview_truncated')}`}
                    </p>
                )}
                {error && <p className="text-sm text-destructive" data-testid="rule-create-error">{error}</p>}

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>{t('business_rules.cancel')}</Button>
                    <Button variant="outline" onClick={test} disabled={preview.isPending} data-testid="rule-create-test">
                        <AppIcon path={preview.isPending ? mdiLoading : mdiFlask} size="sm" className={preview.isPending ? 'motion-safe:animate-spin' : ''} />
                        {t('business_rules.create.test')}
                    </Button>
                    <Button onClick={save} disabled={create.isPending} data-testid="rule-create-submit">
                        {t('business_rules.create.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
